import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { FiServer, FiCloud, FiSettings, FiActivity, FiShield, FiClock, FiDollarSign, FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL;

interface ServiceType {
  id: number;
  name: string;
  has_hosting_details: number;
  hsn_code?: string;
  price?: number | string;
  description?: string;
}

const Showcase: React.FC = () => {
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_URL}/auth/public/services`)
      .then(res => {
        setServiceTypes(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching showcase services:', err);
        setError('Unable to load service plans. Please try again later.');
        setLoading(false);
      });
  }, []);

  // Helper to map icons to service types based on name keywords
  const getServiceIcon = (name: string) => {
    const n = name.toLowerCase();
    if (n.includes('server') || n.includes('dedicated') || n.includes('vps') || n.includes('hosting')) {
      return <FiServer className="text-violet-400 text-3xl" />;
    }
    if (n.includes('cloud') || n.includes('backup') || n.includes('storage')) {
      return <FiCloud className="text-indigo-400 text-3xl" />;
    }
    if (n.includes('license') || n.includes('software') || n.includes('panel')) {
      return <FiSettings className="text-violet-400 text-3xl" />;
    }
    return <FiActivity className="text-indigo-400 text-3xl" />;
  };

  return (
    <div className="min-h-screen bg-[#0b0c16] text-[#e2e8f0] font-sans antialiased overflow-x-hidden selection:bg-violet-600 selection:text-white">
      {/* Background gradients */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-gradient-to-tr from-violet-600/10 to-indigo-600/10 rounded-full blur-[120px] pointer-events-none -z-10" />
      <div className="absolute top-1/3 right-1/4 w-[600px] h-[600px] bg-gradient-to-tr from-indigo-500/10 to-purple-600/10 rounded-full blur-[130px] pointer-events-none -z-10" />

      {/* Header */}
      <header className="border-b border-slate-900 bg-[#07080e]/80 backdrop-blur sticky top-0 z-50 transition-all duration-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/showcase" className="flex flex-col">
              <span className="text-xl font-black tracking-wider text-white bg-clip-text bg-gradient-to-r from-violet-400 to-indigo-400 uppercase">
                Stream Data
              </span>
              <span className="text-[10px] text-violet-400 tracking-widest font-extrabold uppercase mt-0.5">
                DDoS Protected Network
              </span>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <Link 
              to="/customer/login" 
              className="px-4 py-2 text-sm text-slate-300 hover:text-white font-semibold transition"
            >
              Client Login
            </Link>
            <Link 
              to="/login" 
              className="px-4 py-2 text-sm font-bold bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-xl shadow-lg hover:shadow-violet-900/30 transition duration-150"
            >
              Portal Login
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-20 text-center relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-3 py-1 bg-violet-900/30 border border-violet-700/30 text-violet-300 text-xs font-black uppercase rounded-full tracking-wider mb-6">
            ✨ Enterprise NVMe Infrastructure
          </span>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight text-white mb-6 leading-tight max-w-4xl mx-auto">
            High Performance Servers with{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-400 via-fuchsia-400 to-indigo-400">
              Advanced DDoS Protection
            </span>
          </h1>
          <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto font-light leading-relaxed mb-10">
            Deploy scalable virtual private servers, dedicated bare metal servers, and server licensing backed by premium up-streams and guaranteed 99.99% uptime.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a 
              href="#plans" 
              className="px-8 py-3.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white font-bold rounded-xl shadow-xl hover:shadow-violet-900/30 transition flex items-center gap-2"
            >
              Browse Products <FiArrowRight />
            </a>
            <a 
              href="https://www.bgp.tools/as/135682" 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-8 py-3.5 bg-slate-900/60 hover:bg-slate-900 border border-slate-800 hover:border-slate-700 text-slate-300 hover:text-white font-bold rounded-xl transition"
            >
              Our ASN 135682
            </a>
          </div>
        </motion.div>
      </section>

      {/* Pricing / Showcase Plans Section */}
      <section id="plans" className="max-w-7xl mx-auto px-6 py-16 border-t border-slate-900">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-black text-white tracking-tight">Products For All Businesses</h2>
          <p className="text-slate-400 text-sm mt-2">Choose the perfect solution for your business needs.</p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-500"></div>
          </div>
        ) : error ? (
          <div className="p-4 text-center max-w-md mx-auto bg-red-900/20 border border-red-800 text-red-400 rounded-xl">
            {error}
          </div>
        ) : serviceTypes.length === 0 ? (
          <p className="text-center text-slate-500 py-10">No service categories are currently published.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviceTypes.map((st, idx) => (
              <motion.div
                key={st.id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-[#0f1121] border border-slate-900 hover:border-violet-900/40 rounded-3xl p-8 flex flex-col justify-between shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-violet-950/20"
              >
                <div>
                  <div className="w-14 h-14 bg-slate-900/80 rounded-2xl flex items-center justify-center mb-6 shadow">
                    {getServiceIcon(st.name)}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{st.name}</h3>
                  
                  {st.description ? (
                    <div 
                      className="text-slate-400 text-sm font-light leading-relaxed mb-6 space-y-1 html-content"
                      dangerouslySetInnerHTML={{ __html: st.description }}
                    />
                  ) : (
                    <p className="text-slate-500 text-sm font-light leading-relaxed mb-6 italic">
                      Standard {st.name} solutions with robust network configurations.
                    </p>
                  )}
                </div>

                <div className="mt-8 border-t border-slate-900 pt-6">
                  <div className="flex items-baseline gap-1.5 mb-6">
                    <span className="text-xs text-slate-500 uppercase font-black tracking-wider">Starting from</span>
                    <span className="text-2xl font-black text-white">₹{Number(st.price || 0).toLocaleString('en-IN')}</span>
                    <span className="text-xs text-slate-500">/ Cycle</span>
                  </div>
                  <button
                    onClick={() => navigate('/customer/login')}
                    className="w-full py-3.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-2xl text-sm font-bold shadow-md hover:shadow-lg transition duration-150 uppercase tracking-wide"
                  >
                    Avail Plan
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Features Showcase Section */}
      <section className="max-w-7xl mx-auto px-6 py-20 border-t border-slate-900">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <span className="text-violet-400 text-xs font-black uppercase tracking-wider block mb-2">
            Built for performance, security and reliability
          </span>
          <h2 className="text-3xl font-black text-white tracking-tight">
            Enterprise Hosting Infrastructure
          </h2>
          <p className="text-slate-400 text-sm mt-3">
            Deploy VPS and Dedicated Servers powered by enterprise hardware, premium up-streams, and built-in protection.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-[#0f1121]/50 border border-slate-900 rounded-3xl p-8 hover:border-slate-800 transition">
            <div className="w-12 h-12 bg-violet-950/40 rounded-2xl flex items-center justify-center mb-6">
              <FiShield className="text-violet-400 text-2xl" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Real-time DDoS Protection</h3>
            <p className="text-slate-400 text-sm font-light leading-relaxed">
              Equipped with intelligent traffic filtering to safeguard your networks against high-volume and application-layer threats.
            </p>
          </div>

          <div className="bg-[#0f1121]/50 border border-slate-900 rounded-3xl p-8 hover:border-slate-800 transition">
            <div className="w-12 h-12 bg-indigo-950/40 rounded-2xl flex items-center justify-center mb-6">
              <FiClock className="text-indigo-400 text-2xl" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">99.99% Uptime Guarantee</h3>
            <p className="text-slate-400 text-sm font-light leading-relaxed">
              Experience outstanding stability with fully redundant setups and automatic failovers to prevent any service interruptions.
            </p>
          </div>

          <div className="bg-[#0f1121]/50 border border-slate-900 rounded-3xl p-8 hover:border-slate-800 transition">
            <div className="w-12 h-12 bg-violet-950/40 rounded-2xl flex items-center justify-center mb-6">
              <FiDollarSign className="text-violet-400 text-2xl" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Transparent Pricing</h3>
            <p className="text-slate-400 text-sm font-light leading-relaxed">
              We offer premium dedicated server instances and virtual hosting services with no hidden fees, providing the best value.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-900 bg-[#07080e] py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-slate-500 text-xs text-center md:text-left">
            Copyright &copy; 2026 Stream Data Networks. All Rights Reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="http://www.streamdata.network/tos.html" className="text-slate-500 hover:text-slate-400 text-xs transition">
              Terms of Service
            </a>
            <span className="text-slate-800">•</span>
            <a href="https://www.bgp.tools/as/135682" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-400 text-xs transition">
              ASN Info
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Showcase;
